import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCookieState } from '@/helpers'

import {
  Button,
  CloseIcon,
  DragIndicatorIcon,
  Drawer,
  EditIcon,
  InputSearch,
  Switch,
  TableInteractive,
  TableInteractiveProps,
  Text,
} from '@mercai/clever'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'

import { TableConfiguratorProps } from './table-configurator.type'

import styles from './table-configurator.module.css'

export const TableConfigurator = <T,>({
  columns,
  cookieKey,
  initialHidden = [],
  header = <></>,
  ...rest
}: TableConfiguratorProps<T>) => {
  const { t } = useTranslation('componentsTableConfigurator')

  const [isOpen, setIsOpen] = useState(false)

  const [hiddenColumnsRaw, setHiddenColumns] = useCookieState<unknown>(
    cookieKey,
    initialHidden,
  )
  const hiddenColumns = Array.isArray(hiddenColumnsRaw)
    ? (hiddenColumnsRaw as string[])
    : []

  const validHidden = useMemo(() => {
    const keys = new Set(columns.map((c) => c.key))
    return hiddenColumns.filter((k) => keys.has(k))
  }, [hiddenColumns, columns])

  const visibleColumns = useMemo(() => {
    return columns.filter((c) => !validHidden.includes(c.key))
  }, [columns, validHidden])

  const orderCookieKey = `${cookieKey}-order`
  const [persistedOrderRaw, setPersistedOrderRaw] = useCookieState<unknown>(
    orderCookieKey,
    undefined,
  )
  const persistedOrder = Array.isArray(persistedOrderRaw)
    ? (persistedOrderRaw as string[])
    : undefined

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns])
  const fullOrder = useMemo(() => {
    if (persistedOrder && persistedOrder.length) {
      const filtered = persistedOrder.filter((k) => allKeys.includes(k))
      const others = allKeys.filter((k) => !filtered.includes(k))
      return [...filtered, ...others]
    }
    return allKeys
  }, [persistedOrder, allKeys])

  const visibleOrderFromFull = useMemo(() => {
    const visibleKeys = visibleColumns.map((c) => c.key)
    const filtered = fullOrder.filter((k) => visibleKeys.includes(k))
    const missing = visibleKeys.filter((k) => !filtered.includes(k))
    return [...filtered, ...missing]
  }, [fullOrder, visibleColumns])

  const [tempVisibleOrder, setTempVisibleOrder] =
    useState<string[]>(visibleOrderFromFull)
  useEffect(() => {
    setTempVisibleOrder(visibleOrderFromFull)
  }, [visibleOrderFromFull])

  const persistOrder = (next: string[]) => {
    const remaining = fullOrder.filter((k) => !next.includes(k))
    const final = [...next, ...remaining]
    setPersistedOrderRaw(final)
  }

  const [activeId, setActiveId] = useState<string | null>(null)
  const sensors = useSensors(useSensor(PointerSensor))
  const [filterText, setFilterText] = useState('')

  const toggleColumn = (key: string) => {
    if (validHidden.includes(key)) {
      setHiddenColumns(validHidden.filter((k) => k !== key))
    } else {
      setHiddenColumns([...validHidden, key])
    }
  }

  function SortableItem({
    id,
    title,
    checked,
    onToggle,
  }: {
    id: string
    title: string
    checked: boolean
    onToggle: () => void
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id })

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.4 : 1,
      zIndex: isDragging ? 9999 : undefined,
    }

    return (
      <div ref={setNodeRef} style={style} className={styles['column-item']}>
        <div className={styles['column-item-info']}>
          <span
            {...attributes}
            {...listeners}
            className={styles['drag-handle']}
          >
            <DragIndicatorIcon />
          </span>
          <Text>{title}</Text>
        </div>
        <Switch checked={checked} onChange={onToggle} />
      </div>
    )
  }

  const persistVisibleOrder = (visibleOrder: string[]) => {
    const remaining = fullOrder.filter((k) => !visibleOrder.includes(k))
    const final = [...visibleOrder, ...remaining]
    setPersistedOrderRaw(final)
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>{header}</div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            model="round"
            onClick={() => setIsOpen(true)}
            className={styles.button}
          >
            <EditIcon />
          </Button>
        </div>
      </div>

      {(() => {
        const advancedProps = {
          ...(rest as unknown as Record<string, unknown>),
          cookieKey: cookieKey ? `${cookieKey}-table` : undefined,
          columns: visibleColumns,
          columnOrder: tempVisibleOrder,
          onColumnsChange: ({ order }) => {
            persistOrder(order)
          },
        } as TableInteractiveProps<T>

        return (
          <TableInteractive
            key={cookieKey ? `${cookieKey}-table` : undefined}
            {...advancedProps}
            translations={{
              sortBy: t('sortBy'),
              sortAsc: t('sortAsc'),
              sortDesc: t('sortDesc'),
              moveToStart: t('moveToStart'),
              moveToEnd: t('moveToEnd'),
            }}
          />
        )
      })()}

      <Drawer
        isOpen={isOpen}
        onHandleOpen={setIsOpen}
        className={styles.drawer}
      >
        <div className={styles['drawer-header']}>
          <Text variant="text-large-bold" className={styles['drawer-title']}>
            {t('drawerTitle')}
          </Text>
          <Button
            variant="secondary"
            model="round"
            onClick={() => setIsOpen(false)}
            className={styles.button}
          >
            <CloseIcon />
          </Button>
        </div>

        <div className={styles['drawer-content']}>
          <InputSearch
            value={filterText}
            onChange={(value) => setFilterText(String(value))}
            className={styles['drawer-search']}
            translations={{ inputPlaceholder: t('drawerFilterPlaceholder') }}
          />

          <Text variant="title-base" className={styles['drawer-subtitle']}>
            {t('drawerDeployedColumnsLabel')}
          </Text>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) =>
              setActiveId(e.active.id as string)
            }
            onDragEnd={(e: DragEndEvent) => {
              const { active, over } = e
              setActiveId(null)
              if (!over) return
              const oldIndex = tempVisibleOrder.indexOf(active.id as string)
              const newIndex = tempVisibleOrder.indexOf(over.id as string)
              if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                const next = arrayMove(tempVisibleOrder, oldIndex, newIndex)
                setTempVisibleOrder(next)
                persistVisibleOrder(next)
              }
            }}
          >
            <SortableContext
              items={tempVisibleOrder}
              strategy={verticalListSortingStrategy}
            >
              <div>
                {tempVisibleOrder
                  .filter((key) => {
                    if (!filterText) return true
                    const col = columns.find((c) => c.key === key)
                    if (!col) return false
                    const label = `${col.title || ''} ${col.key}`.toLowerCase()
                    return label.includes(filterText.toLowerCase())
                  })
                  .map((key) => {
                    const col = columns.find((c) => c.key === key)
                    if (!col) return null
                    return (
                      <SortableItem
                        key={col.key}
                        id={col.key}
                        title={col.title || col.key}
                        checked={!validHidden.includes(col.key)}
                        onToggle={() => toggleColumn(col.key)}
                      />
                    )
                  })}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeId ? (
                <div
                  className={`${styles['column-item']} ${styles.draggingOverlay}`}
                >
                  <div className={styles['column-item-info']}>
                    <div className={styles['drag-handle']}>
                      <DragIndicatorIcon />
                    </div>
                    <Text>
                      {columns.find((c) => c.key === activeId)?.title ||
                        activeId}
                    </Text>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          <Text variant="title-base" className={styles['drawer-subtitle']}>
            {t('drawerHiddenColumnsLabel')}
          </Text>
          <div className={styles['columns-list']}>
            {columns
              .filter((c) => validHidden.includes(c.key))
              .filter((c) => {
                if (!filterText) return true
                const label = `${c.title || ''} ${c.key}`.toLowerCase()
                return label.includes(filterText.toLowerCase())
              })
              .map((c) => (
                <div key={c.key} className={styles['column-item']}>
                  <Text>{c.title || c.key}</Text>
                  <Switch
                    checked={!validHidden.includes(c.key)}
                    onChange={() => toggleColumn(c.key)}
                  />
                </div>
              ))}
          </div>
          <div className={styles['drawer-actions']}>
            <Button
              variant="ghost"
              onClick={() => setHiddenColumns(initialHidden ?? [])}
            >
              {t('drawerResetDefaultButton')}
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  )
}

export default TableConfigurator
